/**
 * Key under which the timer service keeps the clock of ONE period of ONE game: the game uuid plus the period
 * type and number, e.g. "<game-uuid>_REGULAR_2" or "<game-uuid>_OVERTIME_1". Every period has its own record, so a
 * reload (or a new period) can never pick up the clock of another period.
 *
 * The website reads the same record (le-website-new, use/useTimer.ts), keep both builders in sync.
 */
export function stopwatchKey (gameUuid, currentPeriod) {
  if (!currentPeriod || !currentPeriod.periodNumber) {
    return gameUuid
  }

  return `${gameUuid}_${currentPeriod.periodType ?? 'REGULAR'}_${currentPeriod.periodNumber}`
}

/**
 * After a reload the stored clock is kept as long as it lies inside the range of the current period (the limits
 * themselves are valid: a clock stopped at 00:00.0 or at the full period stays as it was). Only a missing record
 * or a time outside the period range sends the clock back to the start of the period.
 */
export function shouldInitTimeByPeriod ({ hasStoredTime, time, min, max }) {
  if (!hasStoredTime || time === null || time === undefined) {
    return true
  }

  return time > max || time < min
}

/**
 * The range of the clock inside the current period, in ms.
 *
 * Count-down clock (time_reverse, basketball): every period runs from its length down to 0, an overtime from its own
 * length. Count-up clock (football): the clock keeps going, period N runs from (N-1) * periodTime to N * periodTime and
 * an overtime follows after all the regular periods. `start` is where the clock stands when the period begins.
 * A period that is not loaded yet counts as the first regular period, so the range is never NaN.
 */
export function periodBounds ({ isReverse, period, periodTime, overtimePeriodTime, numberOfRegularPeriods }) {
  const type = period?.periodType ?? 'REGULAR'
  const number = period?.periodNumber || 1
  const regular = periodTime ?? 0
  const overtime = overtimePeriodTime ?? 0

  let min
  let max

  if (isReverse) {
    min = 0
    max = type === 'OVERTIME' ? overtime : regular
  } else if (type === 'OVERTIME') {
    min = ((numberOfRegularPeriods ?? 0) * regular) + ((number - 1) * overtime)
    max = ((numberOfRegularPeriods ?? 0) * regular) + (number * overtime)
  } else {
    min = regular * (number - 1)
    max = regular * number
  }

  return { min, max, start: isReverse ? max : min }
}

/**
 * Which of the stopwatch windows runs the clock, decided from the Ably presence members by a rule every window
 * evaluates the same way (so two windows can never both think they are the active one):
 *  - a "stopwatch" window beats the small stopwatch of the live view; among stopwatch windows the NEWEST one wins
 *    (a scorekeeper who opens the stopwatch takes it over, the older window becomes a display);
 *  - with only small stopwatches the OLDEST one keeps the clock, a window that opens later just follows it.
 * Ties are broken by the client id. Returns null when nobody is present.
 */
export function electActiveMember (members) {
  const list = members ?? []
  const stopwatches = list.filter(member => member.data?.type === 'stopwatch')
  const newestWins = stopwatches.length > 0
  const candidates = newestWins ? stopwatches : list

  return candidates.reduce((best, member) => {
    if (!best) {
      return member
    }

    const memberTime = member.timestamp ?? 0
    const bestTime = best.timestamp ?? 0

    if (memberTime === bestTime) {
      return member.clientId > best.clientId ? member : best
    }

    return (newestWins ? memberTime > bestTime : memberTime < bestTime) ? member : best
  }, null)
}
